import { useContext, useEffect, useRef, useState } from 'react'
import { getIdToken } from '../api/token'
import { AppStateContext } from '../state/AppProvider'

type MessageHandler = (type: string, data: any) => void

// Shared WebSocket instance and handlers
const MAX_RETRIES = 3
const messageHandlers = new Set<MessageHandler>()
let sharedSocket: WebSocket | null = null
let reconnectTimeoutRef: number | null = null
let heartbeatInterval: number | null = null
let isConnecting = false
let retryCount = 0
let onReconnectFailedCallback: (() => void) | null = null

const cleanup = () => {
  if (reconnectTimeoutRef) {
    clearTimeout(reconnectTimeoutRef)
    reconnectTimeoutRef = null
  }
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
  }
  if (sharedSocket) {
    sharedSocket.close()
    sharedSocket = null
  }
  isConnecting = false
}

const connect = async (wsTimeout: number) => {
  // Prevent multiple connection attempts - check for OPEN or CONNECTING states
  if (
    isConnecting ||
    (sharedSocket && (sharedSocket.readyState === WebSocket.OPEN || sharedSocket.readyState === WebSocket.CONNECTING))
  ) {
    return
  }

  isConnecting = true
  const forceRefresh = retryCount > 0
  const token = await getIdToken(forceRefresh)

  // Double-check after async call - another component might have connected while we waited
  if (
    sharedSocket &&
    (sharedSocket.readyState === WebSocket.OPEN || sharedSocket.readyState === WebSocket.CONNECTING)
  ) {
    isConnecting = false
    return
  }

  // Detect if running locally
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  const wsUrl = isLocal
    ? `ws://localhost:8080/ws?token=${encodeURIComponent(token)}`
    : `/ws?token=${encodeURIComponent(token)}`

  const calculatedWsTimeout = (wsTimeout - 5) * 1000

  try {
    sharedSocket = new WebSocket(wsUrl)

    sharedSocket.onmessage = event => {
      const message = JSON.parse(event.data)
      // Call all registered handlers
      messageHandlers.forEach(handler => {
        try {
          handler(message.type, message.data)
        } catch (error) {
          console.error('Error in WebSocket message handler:', error)
        }
      })
    }

    sharedSocket.onopen = () => {
      isConnecting = false
      retryCount = 0
      console.info('WebSocket connected')
      // Clear any pending reconnection timeout when connection is established
      if (reconnectTimeoutRef) {
        clearTimeout(reconnectTimeoutRef)
        reconnectTimeoutRef = null
      }
      // Clear any existing heartbeat interval
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval)
        heartbeatInterval = null
      }

      // Set up heartbeat
      const heartbeat = () => {
        if (sharedSocket?.readyState === WebSocket.OPEN) {
          sharedSocket.send(JSON.stringify({ type: 'ping', data: null }))
        }
      }

      heartbeatInterval = window.setInterval(heartbeat, calculatedWsTimeout)
    }

    sharedSocket.onclose = event => {
      isConnecting = false
      console.info('WebSocket closed', event.code, event.reason)
      // Clear heartbeat interval on close
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval)
        heartbeatInterval = null
      }

      if (messageHandlers.size > 0) {
        if (retryCount >= MAX_RETRIES) {
          console.warn('WebSocket: max reconnection attempts reached, stopping')
          retryCount = 0
          onReconnectFailedCallback?.()
          return
        }
        retryCount++
        reconnectTimeoutRef = window.setTimeout(async () => {
          console.log(`Attempting to reconnect (${retryCount}/${MAX_RETRIES})`)
          await connect(wsTimeout)
        }, calculatedWsTimeout)
      }
    }

    sharedSocket.onerror = error => {
      isConnecting = false
      console.error('WebSocket error:', error)
    }
  } catch (error) {
    isConnecting = false
    console.error('Failed to create WebSocket:', error)
  }
}

export const useWebSocket = ({ onMessage }: { onMessage: MessageHandler }) => {
  const [isConnected, setIsConnected] = useState(false)
  const handlerRef = useRef<MessageHandler>(onMessage)
  const wrapperRef = useRef<MessageHandler | null>(null)
  const appState = useContext(AppStateContext)
  const wsTimeout = appState?.state.frontendSettings?.ws?.timeout ?? 30

  // Update handler ref when it changes
  useEffect(() => {
    handlerRef.current = onMessage
  }, [onMessage])

  useEffect(() => {
    onReconnectFailedCallback = () => {
      appState?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: {
          message: 'Connection lost. Please refresh the page.',
          severity: 'warning',
        },
      })
    }
    return () => {
      onReconnectFailedCallback = null
    }
  }, [appState])

  // Clean up on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanup()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    // Remove old wrapper if it exists
    if (wrapperRef.current) {
      messageHandlers.delete(wrapperRef.current)
    }

    // Create a wrapper function that always calls the current ref value
    // This allows us to properly add/remove the handler from the set
    const handlerWrapper: MessageHandler = (type, data) => {
      handlerRef.current(type, data)
    }

    // Store the wrapper in a ref so we can remove it later
    wrapperRef.current = handlerWrapper

    // Register this handler
    messageHandlers.add(handlerWrapper)

    // Connect if not already connected or connecting
    if (
      !sharedSocket ||
      sharedSocket.readyState === WebSocket.CLOSED ||
      sharedSocket.readyState === WebSocket.CLOSING
    ) {
      // Only connect if not already in the process of connecting
      if (!isConnecting) {
        connect(wsTimeout)
      }
    } else {
      setIsConnected(sharedSocket.readyState === WebSocket.OPEN)
    }

    // Update connection status periodically
    const statusInterval = setInterval(() => {
      if (sharedSocket) {
        setIsConnected(sharedSocket.readyState === WebSocket.OPEN)
      }
    }, 1000)

    return () => {
      // Unregister this handler
      if (wrapperRef.current) {
        messageHandlers.delete(wrapperRef.current)
        wrapperRef.current = null
      }
      clearInterval(statusInterval)

      // If no handlers are left, clean up the connection
      if (messageHandlers.size === 0) {
        cleanup()
      }
    }
  }, [wsTimeout])

  const sendMessage = (message: object) => {
    if (sharedSocket?.readyState === WebSocket.OPEN) {
      sharedSocket.send(JSON.stringify({ type: 'message', data: message }))
    }
  }

  return { isConnected, sendMessage }
}
